import type { IconProps } from "@tabler/icons-react";
import {
  IconActivity,
  IconApi,
  IconBolt,
  IconBrandGoogle,
  IconBuilding,
  IconBuildingFactory,
  IconBuildingFactory2,
  IconBuildings,
  IconCalendar,
  IconChartArea,
  IconChartBar,
  IconChartLine,
  IconChartPie,
  IconClock,
  IconDashboard,
  IconDatabase,
  IconDeviceAnalytics,
  IconDeviceDesktop,
  IconDevices,
  IconFolder,
  IconGauge,
  IconHome,
  IconHospital,
  IconNetwork,
  IconReport,
  IconSchool,
  IconServer,
  IconSettings,
  IconShoppingCart,
  IconTarget,
  IconTrendingUp,
  IconUsers,
} from "@tabler/icons-react";

export type DashboardIconName =
  | "chart-bar"
  | "dashboard"
  | "chart-line"
  | "chart-area"
  | "chart-pie"
  | "device-analytics"
  | "report"
  | "trending-up"
  | "activity"
  | "target"
  | "building"
  | "users"
  | "calendar"
  | "clock"
  | "server"
  | "database"
  | "api"
  | "network"
  | "devices"
  | "monitor"
  | "gauge"
  | "speedometer"
  | "analytics"
  | "factory"
  | "hospital"
  | "school"
  | "shopping-cart"
  | "home"
  | "office"
  | "settings"
  | "bolt"
  | "folder";

export interface DashboardIcon {
  name: DashboardIconName;
  label: string;
  category: "general" | "business" | "technical" | "industry";
  component: React.ComponentType<IconProps>;
}

/**
 * Curated registry of dashboard icons organized by category
 */
export const DASHBOARD_ICONS: Record<DashboardIconName, DashboardIcon> = {
  // General Analytics & Dashboards
  dashboard: {
    name: "dashboard",
    label: "Dashboard",
    category: "general",
    component: IconDashboard,
  },
  "chart-bar": {
    name: "chart-bar",
    label: "Bar Chart",
    category: "general",
    component: IconChartBar,
  },
  "chart-line": {
    name: "chart-line",
    label: "Line Chart",
    category: "general",
    component: IconChartLine,
  },
  "chart-area": {
    name: "chart-area",
    label: "Area Chart",
    category: "general",
    component: IconChartArea,
  },
  "chart-pie": {
    name: "chart-pie",
    label: "Pie Chart",
    category: "general",
    component: IconChartPie,
  },
  "device-analytics": {
    name: "device-analytics",
    label: "Analytics",
    category: "general",
    component: IconDeviceAnalytics,
  },
  report: {
    name: "report",
    label: "Reports",
    category: "general",
    component: IconReport,
  },
  "trending-up": {
    name: "trending-up",
    label: "Trends",
    category: "general",
    component: IconTrendingUp,
  },
  activity: {
    name: "activity",
    label: "Activity",
    category: "general",
    component: IconActivity,
  },
  target: {
    name: "target",
    label: "Goals",
    category: "general",
    component: IconTarget,
  },

  // Business & Operations
  building: {
    name: "building",
    label: "Building",
    category: "business",
    component: IconBuilding,
  },
  users: {
    name: "users",
    label: "Users",
    category: "business",
    component: IconUsers,
  },
  calendar: {
    name: "calendar",
    label: "Calendar",
    category: "business",
    component: IconCalendar,
  },
  clock: {
    name: "clock",
    label: "Time Tracking",
    category: "business",
    component: IconClock,
  },
  office: {
    name: "office",
    label: "Office",
    category: "business",
    component: IconBuildings,
  },

  // Technical & Infrastructure
  server: {
    name: "server",
    label: "Server",
    category: "technical",
    component: IconServer,
  },
  database: {
    name: "database",
    label: "Database",
    category: "technical",
    component: IconDatabase,
  },
  api: {
    name: "api",
    label: "API",
    category: "technical",
    component: IconApi,
  },
  network: {
    name: "network",
    label: "Network",
    category: "technical",
    component: IconNetwork,
  },
  devices: {
    name: "devices",
    label: "Devices",
    category: "technical",
    component: IconDevices,
  },
  monitor: {
    name: "monitor",
    label: "Monitoring",
    category: "technical",
    component: IconDeviceDesktop,
  },
  gauge: {
    name: "gauge",
    label: "Metrics",
    category: "technical",
    component: IconGauge,
  },
  speedometer: {
    name: "speedometer",
    label: "Performance",
    category: "technical",
    component: IconBuildingFactory,
  },
  analytics: {
    name: "analytics",
    label: "System Analytics",
    category: "technical",
    component: IconBrandGoogle,
  },
  settings: {
    name: "settings",
    label: "Configuration",
    category: "technical",
    component: IconSettings,
  },
  bolt: {
    name: "bolt",
    label: "Power & Energy",
    category: "technical",
    component: IconBolt,
  },

  // Industry Specific
  factory: {
    name: "factory",
    label: "Manufacturing",
    category: "industry",
    component: IconBuildingFactory2,
  },
  hospital: {
    name: "hospital",
    label: "Healthcare",
    category: "industry",
    component: IconHospital,
  },
  school: {
    name: "school",
    label: "Education",
    category: "industry",
    component: IconSchool,
  },
  "shopping-cart": {
    name: "shopping-cart",
    label: "Retail",
    category: "industry",
    component: IconShoppingCart,
  },
  home: {
    name: "home",
    label: "Residential",
    category: "industry",
    component: IconHome,
  },
  folder: {
    name: "folder",
    label: "Folder",
    category: "general",
    component: IconFolder,
  },
};

/**
 * Default dashboard icon
 */
export const DEFAULT_DASHBOARD_ICON: DashboardIconName = "dashboard";

/**
 * Get icon component by name with fallback to default
 */
export function getDashboardIcon(
  iconName?: string
): React.ComponentType<IconProps> {
  if (!iconName || !DASHBOARD_ICONS[iconName as DashboardIconName]) {
    return DASHBOARD_ICONS[DEFAULT_DASHBOARD_ICON].component;
  }
  return DASHBOARD_ICONS[iconName as DashboardIconName].component;
}

/**
 * Get icon metadata by name
 */
export function getDashboardIconMeta(iconName?: string): DashboardIcon {
  if (!iconName || !DASHBOARD_ICONS[iconName as DashboardIconName]) {
    return DASHBOARD_ICONS[DEFAULT_DASHBOARD_ICON];
  }
  return DASHBOARD_ICONS[iconName as DashboardIconName];
}

/**
 * Get all icons grouped by category
 */
export function getDashboardIconsByCategory(): Record<string, DashboardIcon[]> {
  const grouped: Record<string, DashboardIcon[]> = {
    general: [],
    business: [],
    technical: [],
    industry: [],
  };

  Object.values(DASHBOARD_ICONS).forEach((icon) => {
    grouped[icon.category].push(icon);
  });

  return grouped;
}

/**
 * Get list of all available icon names
 */
export function getAllDashboardIconNames(): DashboardIconName[] {
  return Object.keys(DASHBOARD_ICONS) as DashboardIconName[];
}

/**
 * Validate if an icon name exists in registry
 */
export function isValidDashboardIcon(
  iconName: string
): iconName is DashboardIconName {
  return iconName in DASHBOARD_ICONS;
}
